import React from 'react';

import { post } from '../api/api';
import { BadgeTab } from '../ui/ui-badge-nav';
import { MenuTab } from '../ui/ui-icon-nav';
import * as WebRoutes from '../web-routes';

import { CELLO_MENU_DATA } from './Cello';
import {
  ACCOUNT_NAV_ICON_NAMES,
  ICON_NAMES,
  LEFT_HAND_NAV_ICON_NAMES,
} from './material-design-icons';
import { EMPLOYEE_PERMISSION, HIRING_PERMISSION } from './permissions';
import { USER_FLOW_IDS, UserflowController } from './userflow-controller';

type TabSpecialProperties = {
  [tabKey: string]: { onClick?: () => void; children?: React.ReactElement };
};

/**
 * The order of tabs in each array in this file is important, as they will be displayed in order
 */

export const orgUserTabs = (
  user: AuthenticatedUser,
  orgId: number,
  specialProperties: TabSpecialProperties = {},
): Array<MenuTab> => {
  const hasEmployeePermissions = user.employeesPermission !== EMPLOYEE_PERMISSION.NONE;
  const hasAllEmployeePermissions = user.employeesPermission === EMPLOYEE_PERMISSION.ALL;
  const hasHiringPermissions = user.hiringPermission === HIRING_PERMISSION.ALL;
  const hasAutomationsPermissions = hasAllEmployeePermissions || hasHiringPermissions;

  return [
    {
      key: 'jobs',
      label: 'Jobs',
      route: WebRoutes.orgJobsRoute(orgId),
      iconName: LEFT_HAND_NAV_ICON_NAMES.JOBS,
      hasPermissions: hasHiringPermissions,
      orgId,
    },
    {
      key: 'automations',
      label: 'Automations',
      route: WebRoutes.orgAutomationsRoute(orgId),
      iconName: LEFT_HAND_NAV_ICON_NAMES.AUTOMATIONS,
      hasPermissions: hasAutomationsPermissions,
      orgId,
    },
    {
      key: 'messages',
      label: 'Messages',
      route: WebRoutes.employeeMessagesRoute({ orgId, employeeId: null }),
      iconName: LEFT_HAND_NAV_ICON_NAMES.MESSAGES,
      hasPermissions: hasEmployeePermissions,
      orgId,
      children: specialProperties.messages?.children,
    },
    {
      key: 'directory',
      label: 'Directory',
      route: WebRoutes.employeesRoute(orgId),
      iconName: LEFT_HAND_NAV_ICON_NAMES.DIRECTORY,
      hasPermissions: hasEmployeePermissions,
      orgId,
    },
    {
      key: 'referrals',
      label: 'Referrals',
      route: WebRoutes.employeeReferralsRoute(orgId),
      iconName: LEFT_HAND_NAV_ICON_NAMES.REFERRALS,
      hasPermissions: hasAllEmployeePermissions && hasHiringPermissions,
      orgId,
    },
    {
      key: 'surveys',
      label: 'Surveys',
      route: WebRoutes.surveysRoute(orgId, null),
      iconName: LEFT_HAND_NAV_ICON_NAMES.SURVEYS,
      hasPermissions: hasEmployeePermissions,
      orgId,
    },
    {
      key: 'backToAccounts',
      label: 'Back to Accounts',
      route: WebRoutes.HOME_ROUTE,
      iconName: ICON_NAMES.BACK_ARROW,
      hasPermissions: !!user.accountGroupId,
      orgId,
      className: 'u-border-top u-leftNav-divider',
    },
  ];
};

export const legacyOrgUserTabs = (
  user: AuthenticatedUser,
  orgId: number,
  specialProperties: TabSpecialProperties = {},
): Array<MenuTab> => {
  const hasEmployeePermissions = user.employeesPermission !== EMPLOYEE_PERMISSION.NONE;
  const hasAllEmployeePermissions = user.employeesPermission === EMPLOYEE_PERMISSION.ALL;
  const hasHiringPermissions = user.hiringPermission === HIRING_PERMISSION.ALL;
  const hasAutomationsPermissions = hasAllEmployeePermissions || hasHiringPermissions;

  return [
    {
      key: 'jobs',
      label: 'Jobs',
      route: WebRoutes.orgJobsRoute(orgId),
      iconName: LEFT_HAND_NAV_ICON_NAMES.JOBS,
      hasPermissions: hasHiringPermissions,
      orgId,
    },
    {
      key: 'automations',
      label: 'Automations',
      route: WebRoutes.orgAutomationsRoute(orgId),
      iconName: LEFT_HAND_NAV_ICON_NAMES.AUTOMATIONS,
      hasPermissions: hasAutomationsPermissions,
      orgId,
    },
    {
      key: 'messages',
      label: 'Messages',
      route: WebRoutes.employeeMessagesRoute({ orgId, employeeId: null }),
      iconName: LEFT_HAND_NAV_ICON_NAMES.MESSAGES,
      hasPermissions: hasEmployeePermissions,
      orgId,
      children: specialProperties.messages?.children,
    },
    {
      key: 'announcements',
      label: 'Announcements',
      route: WebRoutes.employeeAnnouncementsRoute(orgId, null),
      iconName: LEFT_HAND_NAV_ICON_NAMES.ANNOUNCEMENTS,
      hasPermissions: hasEmployeePermissions,
      orgId,
    },
    {
      key: 'directory',
      label: 'Directory',
      route: WebRoutes.employeesRoute(orgId),
      iconName: LEFT_HAND_NAV_ICON_NAMES.DIRECTORY,
      hasPermissions: hasEmployeePermissions,
      orgId,
    },
    {
      key: 'referrals',
      label: 'Referrals',
      route: WebRoutes.employeeReferralsRoute(orgId),
      iconName: LEFT_HAND_NAV_ICON_NAMES.REFERRALS,
      hasPermissions: hasAllEmployeePermissions && hasHiringPermissions,
      orgId,
    },
    {
      key: 'surveys',
      label: 'Surveys',
      route: WebRoutes.surveysRoute(orgId, null),
      iconName: LEFT_HAND_NAV_ICON_NAMES.SURVEYS,
      hasPermissions: hasEmployeePermissions,
      orgId,
    },
    {
      key: 'backToAccounts',
      label: 'Back to Accounts',
      route: WebRoutes.HOME_ROUTE,
      iconName: ICON_NAMES.BACK_ARROW,
      hasPermissions: !!user.accountGroupId,
      orgId,
      className: 'u-border-top u-leftNav-divider',
    },
  ];
};

export const orgAdminTabs = (user: AuthenticatedUser, orgId: number): Array<MenuTab> => [
  {
    key: 'userFlow',
    label: 'Take a Tour',
    iconName: LEFT_HAND_NAV_ICON_NAMES.TAKE_A_TOUR,
    hasPermissions: true,
    orgId,
    onClick: async () =>
      UserflowController.beginUserFlow(
        user.hiringPermission === 'ALL'
          ? USER_FLOW_IDS.DASHBOARD_PHASE1_HIRING
          : USER_FLOW_IDS.DASHBOARD_PHASE1_EMPLOYEE,
      ),
  },
  {
    key: 'celloIntegration',
    label: 'Earn $200',
    iconName: LEFT_HAND_NAV_ICON_NAMES.REFERRAL_PROGRAM,
    hasPermissions: true,
    orgId,
    customAttribute: CELLO_MENU_DATA,
    className: 'cello-container',
    // Ideally we would post the ucc token immediately after the cello.boot function completes
    // But there doesn't seem to be a great way to do that
    // Assuming clicking this tab is the only way a user can view their UCC token/link this should capture what we need
    onClick: async () => {
      const { ucc } = await Cello('getActiveUcc');
      if (ucc && !user.CelloUccs.find((celloUcc) => celloUcc.celloUcc === ucc)) {
        post('/cello/ucc', { body: JSON.stringify({ ucc }) });
      }
    },
  },
  {
    key: 'settings',
    label: 'Settings',
    route: WebRoutes.orgSettingsRoute(orgId),
    iconName: LEFT_HAND_NAV_ICON_NAMES.COMPANY_SETTINGS,
    hasPermissions: true,
    orgId,
  },
  {
    key: 'adminPanel',
    label: 'Admin Panel',
    route: WebRoutes.orgAdminRoute(orgId),
    iconName: LEFT_HAND_NAV_ICON_NAMES.ADMIN_PANEL,
    hasPermissions: user.isAdmin,
    orgId,
  },
];

export const allOrgTabs = (
  user: AuthenticatedUser,
  orgId: number,
  specialProperties: TabSpecialProperties = {},
): Array<MenuTab> => orgUserTabs(user, orgId, specialProperties).concat(orgAdminTabs(user, orgId));

export const jobSettingsTabs = (orgId: number, jobId?: number | null) => [
  {
    key: 'notifications',
    label: 'Notifications',
    route: jobId
      ? WebRoutes.jobSettingsNotificationsRoute(orgId, jobId)
      : WebRoutes.orgJobSettingsNotificationsRoute(orgId),
    hasPermissions: true,
    jobId,
    orgId,
    whitelistedQueryParams: ['jobStatus'],
  },
  {
    key: 'recentActivity',
    label: 'Recent Activity',
    route: jobId
      ? WebRoutes.jobSettingsRecentActivityRoute(orgId, jobId)
      : WebRoutes.orgJobSettingsRecentActivityRoute(orgId),
    hasPermissions: true,
    jobId,
    orgId,
    whitelistedQueryParams: ['jobStatus'],
  },
  {
    key: 'history',
    label: 'Job History',
    route: jobId
      ? WebRoutes.jobSettingsHistoryRoute(orgId, jobId)
      : WebRoutes.orgJobSettingsHistoryRoute(orgId),
    hasPermissions: true,
    jobId,
    whitelistedQueryParams: ['jobStatus'],
  },
];

// We might be able to clean up whitelisted params in 3768
export const jobTabs = (
  orgId: number,
  jobId?: number | null,
  badgeCounts: { [tabKey: string]: number | null } = {},
): Array<BadgeTab> => [
  {
    key: 'messages',
    label: 'Messages',
    route: jobId ? WebRoutes.jobMessagesRoute(orgId, jobId) : WebRoutes.orgMessagesRoute(orgId),
    hasPermissions: true,
    jobId,
    orgId,
    count: badgeCounts.messages || 0,
    whitelistedQueryParams: ['jobStatus'],
    badgeStyle: 'alert',
  },
  {
    key: 'needReview',
    label: 'Need Review',
    route: jobId
      ? WebRoutes.jobNeedReviewRoute(orgId, jobId)
      : WebRoutes.orgNeedsReviewRoute(orgId),
    hasPermissions: true,
    jobId,
    count: badgeCounts.needReview || 0,
    badgeStyle: 'alert',
    whitelistedQueryParams: ['jobStatus'],
  },
  {
    key: 'inProgress',
    label: 'In Progress',
    route: jobId ? WebRoutes.jobInProgressRoute(orgId, jobId) : WebRoutes.orgInProgressRoute(orgId),
    hasPermissions: true,
    jobId,
    orgId,
    count: badgeCounts.inProgress || 0,
    whitelistedQueryParams: ['jobStatus'],
  },
  {
    key: 'saved',
    label: 'Saved',
    route: jobId ? WebRoutes.jobSavedRoute(orgId, jobId) : WebRoutes.orgSavedRoute(orgId),
    hasPermissions: true,
    jobId,
    orgId,
    count: badgeCounts.saved || 0,
    whitelistedQueryParams: ['jobStatus'],
  },
  {
    key: 'rejected',
    label: 'Rejected',
    route: jobId ? WebRoutes.jobRejectedRoute(orgId, jobId) : WebRoutes.orgRejectedRoute(orgId),
    hasPermissions: true,
    jobId,
    orgId,
    count: badgeCounts.rejected || 0,
    whitelistedQueryParams: ['jobStatus'],
  },
  {
    key: 'expired',
    label: 'Expired',
    route: jobId ? WebRoutes.jobExpiredRoute(orgId, jobId) : WebRoutes.orgExpiredRoute(orgId),
    hasPermissions: true,
    jobId,
    orgId,
    count: badgeCounts.expired || 0,
    whitelistedQueryParams: ['jobStatus'],
  },
];

export const jobBackTab = (orgId: number, jobId?: number | null) => ({
  key: 'back',
  label: 'Back to Jobs',
  route: WebRoutes.orgJobsRoute(orgId),
  hasPermissions: true,
  jobId,
  orgId,
});

export const accountTabs = (
  accountId: number,
  orgId?: number,
  previousLocationPath?: string,
): Array<MenuTab> => [
  {
    key: 'accountProfile',
    label: 'Account Profile',
    iconName: ACCOUNT_NAV_ICON_NAMES.ACCOUNT_PROFILE,
    route: WebRoutes.accountProfile(accountId),
    hasPermissions: true,
    orgId,
    previousLocationPath,
  },
  {
    key: 'users',
    label: 'Users',
    iconName: ACCOUNT_NAV_ICON_NAMES.USERS,
    route: WebRoutes.accountUser(accountId),
    hasPermissions: true,
    orgId,
    previousLocationPath,
  },
  {
    key: 'billing',
    label: 'Billing',
    iconName: ACCOUNT_NAV_ICON_NAMES.BILLING,
    route: WebRoutes.accountBilling(accountId),
    hasPermissions: true,
    orgId,
    previousLocationPath,
  },
  {
    key: 'organizations',
    label: 'Organizations',
    iconName: ACCOUNT_NAV_ICON_NAMES.ORGANIZATIONS,
    route: WebRoutes.accountOrganizations(accountId),
    hasPermissions: true,
    orgId,
    previousLocationPath,
  },
  {
    key: 'carrierRegistration',
    label: 'Carrier Registration',
    iconName: ACCOUNT_NAV_ICON_NAMES.CARRIER_REGISTRATION,
    route: WebRoutes.accountCarrierRegistration(accountId),
    hasPermissions: true,
    orgId,
    previousLocationPath,
  },
  {
    key: 'back',
    label: 'Back',
    iconName: ICON_NAMES.BACK_ARROW,
    route: previousLocationPath || (orgId ? WebRoutes.orgRoute(orgId) : WebRoutes.HOME_ROUTE),
    hasPermissions: true,
    orgId,
    className: 'u-border-top u-leftNav-divider',
  },
];

export const getTabName = (
  pathname: string,
  context: Array<'org' | 'job' | 'account'>,
  routeParams: {
    user?: AuthenticatedUser;
    accountId?: number | null;
    orgId?: number;
    jobId?: number | null;
  },
) => {
  const { user, orgId, accountId, jobId } = routeParams;

  const orgLevelTabs = context.includes('org') && user && orgId ? allOrgTabs(user, orgId) : [];
  const jobLevelTabs = context.includes('job') && orgId ? jobTabs(orgId, jobId) : [];
  const accountLevelTabs =
    context.includes('account') && accountId ? accountTabs(accountId, orgId) : [];

  const allTabs = [...orgLevelTabs, ...jobLevelTabs, ...accountLevelTabs];
  return allTabs.find((tab) => tab.route === pathname)?.label;
};
